/**
 * Customer review submit modal + product reviews section (Feb 2026, Slice 2).
 *
 * Two exports:
 *   <ReviewSubmitModal /> — open when a customer taps "Review this
 *     product". Uses MediaUploader for photos/videos with the "3× more
 *     likes" nudge. Pre-fills existing review body if the customer has
 *     already reviewed (edit mode).
 *
 *   <ProductReviewsSection /> — renders on product detail page. Shows
 *     approved reviews with author display name, rating, body, media,
 *     and (Slice 4) like button + count.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { Star, X, Camera, Flame } from 'lucide-react';
import { toast } from 'sonner';
import MediaUploader, { MediaPreviewRow } from '../MediaUploader';
import { formatDateET } from '../../lib/timeET';
import { useReviewRewards } from '../../hooks/useReviewRewards';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

// Code (terminal) vs Modern (cream/emerald) class sets.
const SKIN = {
  code: {
    fireOn: 'border-[#FF6B00] bg-[#FF6B00]/10 text-[#FF6B00]', fireOff: 'border-[#2A0F3D] text-[#A3C7A8] hover:border-[#FF6B00] hover:text-[#FF6B00]', fireShape: '',
    starOff: 'text-[#4B6B52]', starDim: 'text-[#2A0F3D]',
    panel: 'bg-[#0C0612] border border-[#2A0F3D]', title: 'text-[#39FF14] font-mono text-sm font-bold uppercase', sub: 'text-[#A3C7A8] font-mono text-xs',
    label: 'text-[10px] font-mono uppercase text-[#4B6B52]', close: 'text-[#A3C7A8] hover:text-[#39FF14]',
    input: 'bg-[#050003] border border-[#2A0F3D] text-[#39FF14] font-mono text-sm p-2 focus:border-[#39FF14] placeholder:text-[#4B6B52]',
    nudge: 'text-[10px] font-mono text-[#B400FF] italic',
    submit: 'bg-[#39FF14] text-[#050003] font-mono text-sm font-bold uppercase tracking-wider py-3 hover:bg-[#5FFF3F]',
    empty: 'border border-[#2A0F3D] p-4', emptyText: 'text-[#4B6B52] font-mono text-xs text-center',
    card: 'border border-[#2A0F3D] p-3', author: 'text-[#39FF14] font-mono text-xs font-bold', date: 'text-[10px] font-mono text-[#4B6B52]',
    body: 'text-[#A3C7A8] font-mono text-sm', avg: 'text-[#A3C7A8] font-mono text-xs', img: 'border border-[#2A0F3D]',
    emptyCopy: '// no reviews yet — be the first to drop one', heading: (n) => `Reviews · ${n}`,
  },
  modern: {
    fireOn: 'border-[#F97316] bg-[#FFF7ED] text-[#EA580C]', fireOff: 'border-[#EFEDE7] text-[#57534E] hover:border-[#F97316] hover:text-[#EA580C]', fireShape: 'rounded-full',
    starOff: 'text-[#D6D3CD]', starDim: 'text-[#E7E5E0]',
    panel: 'bg-white border border-[#EFEDE7] rounded-t-2xl sm:rounded-2xl shadow-xl', title: 'text-[#111614] text-base font-semibold', sub: 'text-[#8E8983] text-xs',
    label: 'text-[11px] font-semibold uppercase tracking-wide text-[#8E8983]', close: 'text-[#8E8983] hover:text-[#111614]',
    input: 'bg-[#FBFAF7] border border-[#EFEDE7] rounded-xl text-[#111614] text-sm p-3 focus:border-[#047857] placeholder:text-[#A8A29E]',
    nudge: 'text-[11px] text-[#047857]',
    submit: 'bg-[#047857] text-white text-sm font-semibold rounded-full py-3 hover:bg-[#065F46]',
    empty: 'border border-dashed border-[#E7E5E0] rounded-xl p-4', emptyText: 'text-[#8E8983] text-sm text-center',
    card: 'border border-[#EFEDE7] rounded-xl p-3 bg-white', author: 'text-[#111614] text-sm font-semibold', date: 'text-[11px] text-[#8E8983]',
    body: 'text-[#3A3934] text-sm leading-relaxed', avg: 'text-[#57534E] text-sm', img: 'rounded-lg border border-[#EFEDE7]',
    emptyCopy: 'No reviews yet — be the first to share one.', heading: (n) => `${n} review${n === 1 ? '' : 's'}`,
  },
};
const skin = (modern) => (modern ? SKIN.modern : SKIN.code);

// TikTok-style bouncy fire like button. Local optimistic state + server
// truth on response. Debounced against tap-spam: while a request is in
// flight we ignore further taps for this review. Server enforces the
// real one-like-per-user rule via a unique index, so worst case a
// double-tap collapses to a toggle-noop.
function FireLikeButton({ review, testId, modern }) {
  const k = skin(modern);
  const [liked, setLiked] = useState(!!review.liked_by_me);
  const [count, setCount] = useState(Number(review.like_count || 0));
  const [busy, setBusy] = useState(false);
  const [bump, setBump] = useState(0);   // bump animation trigger

  const onTap = useCallback(async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    // Optimistic
    const nextLiked = !liked;
    setLiked(nextLiked);
    setCount((c) => Math.max(0, c + (nextLiked ? 1 : -1)));
    setBump((n) => n + 1);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/customer/reviews/${review.id}/like`,
        {},
        { withCredentials: true },
      );
      setLiked(!!data.liked_by_me);
      setCount(Number(data.like_count || 0));
    } catch (err) {
      // Roll back
      setLiked(!nextLiked);
      setCount((c) => Math.max(0, c + (nextLiked ? -1 : 1)));
      const msg = err.response?.data?.detail || 'Could not update like';
      if (err.response?.status === 401) {
        toast.error('Log in to drop a 🔥');
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  }, [busy, liked, review.id]);

  return (
    <button
      type="button"
      onClick={onTap}
      disabled={busy}
      className={`inline-flex items-center gap-1 px-2 py-1 border transition-all duration-150 select-none ${k.fireShape} ${liked ? k.fireOn : k.fireOff}`}
      data-testid={testId}
      aria-label={liked ? 'Unlike review' : 'Like review'}
      aria-pressed={liked}
    >
      <Flame
        key={bump}
        className={`w-4 h-4 ${liked ? 'fill-[#FF6B00]' : ''} review-fire-bump`}
      />
      <span className="font-mono text-xs font-bold tabular-nums">{count}</span>
    </button>
  );
}

function StarPicker({ value, onChange, size = 'w-6 h-6', modern }) {
  return (
    <div className="flex gap-1" data-testid="review-star-picker">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className="focus:outline-none"
          data-testid={`review-star-${n}`}
          aria-label={`${n} star${n > 1 ? 's' : ''}`}
        >
          <Star
            className={`${size} ${n <= value ? 'fill-[#FFD60A] text-[#FFD60A]' : skin(modern).starOff}`}
          />
        </button>
      ))}
    </div>
  );
}

function StarDisplay({ value, size = 'w-4 h-4', modern }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`${size} ${n <= value ? 'fill-[#FFD60A] text-[#FFD60A]' : skin(modern).starDim}`} />
      ))}
    </div>
  );
}

export function ReviewSubmitModal({ productId, productName, onClose, onSubmitted, modern = false }) {
  const k = skin(modern);
  const queryClient = useQueryClient();
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState('');
  const [media, setMedia] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const photoLimit = useReviewRewards(true)?.photoLimit ?? 1;
  useEffect(() => {
    if (!productId) return;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/customer/reviews/mine?product_id=${productId}`,
          { withCredentials: true },
        );
        if (data.review) {
          setRating(data.review.rating || 5);
          setBody(data.review.body || '');
          const ids = data.review.media_ids || [];
          setMedia(ids.map((id) => ({ id, url: `/api/uploads/media/${id}`, kind: 'image' })));
        }
      } catch { /* first-time review — no existing doc, fine */ }
      finally { setLoaded(true); }
    })();
  }, [productId]);

  const submit = useCallback(async () => {
    const trimmed = body.trim();
    if (!trimmed) { toast.error('Please write a few words about your experience'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/customer/reviews`,
        { product_id: productId, rating, body: trimmed, media_ids: media.map((m) => m.id) },
        { withCredentials: true },
      );
      toast.success('Thanks! Your review is under moderation — we approve within 24 h.');
      queryClient.invalidateQueries({ queryKey: ['review-window'] });
      onSubmitted?.(data.review);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally { setSaving(false); }
  }, [body, rating, media, productId, onSubmitted, onClose, queryClient]);

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 flex items-end sm:items-center justify-center"
      onClick={onClose}
      data-testid="review-submit-modal"
    >
      <div
        className={`w-full sm:max-w-md ${k.panel} p-4 max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className={k.title}>{modern ? 'Write a review' : 'Post a review'}</p>
            <p className={`${k.sub} mt-0.5 truncate`}>{productName || 'Product'}</p>
          </div>
          <button type="button" onClick={onClose} data-testid="review-submit-close"
            className={k.close}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-3">
          <p className={`${k.label} mb-1`}>Rating</p>
          <StarPicker value={rating} onChange={setRating} modern={modern} />
        </div>

        <div className="mb-3">
          <p className={`${k.label} mb-1`}>Your take</p>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Fire? Mid? Honest reviews help other customers pick right."
            rows={4}
            maxLength={4000}
            className={`w-full focus:outline-none ${k.input}`}
            data-testid="review-body-input"
          />
        </div>

        {photoLimit > 0 && (
        <div className="mb-3" data-testid="review-photo-section">
          <p className={`${k.label} mb-1 flex items-center gap-1`}>
            <Camera className="w-3 h-3" /> {photoLimit === 1 ? 'Photo (1 max)' : `Photos (up to ${photoLimit})`}
          </p>
          <div className="flex flex-col gap-2">
            <MediaUploader
              accept="image"
              maxCount={photoLimit}
              label="Add photo"
              currentCount={media.length}
              testIdPrefix="review-media-uploader"
              modern={modern}
              onUploaded={(f) => setMedia((prev) => [...prev, { ...f, fresh: true }])}
            />
            <p className={k.nudge}>
              📸 Reviews with photos get 3× more likes — more likes = more points 🔥
            </p>
            <MediaPreviewRow
              items={media}
              onRemove={(id) => {
                if (media.find((m) => m.id === id)?.fresh) {
                  axios.post(`${API_URL}/api/uploads/media/${id}/delete`, {}, { withCredentials: true }).catch(() => {});
                }
                setMedia((prev) => prev.filter((m) => m.id !== id));
              }}
              testIdPrefix="review-media-preview"
            />
          </div>
        </div>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={saving || !loaded}
          className={`w-full disabled:opacity-40 ${k.submit}`}
          data-testid="review-submit-btn"
        >
          {saving ? 'Submitting…' : 'Submit review'}
        </button>
      </div>
    </div>
  );
}

export function ProductReviewsSection({ productId, modern = false }) {
  const k = skin(modern);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/products/${productId}/reviews`, {
          withCredentials: true,
        });
        setReviews(data.reviews || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, [productId]);

  if (loading) return null;
  if (!reviews.length) {
    return (
      <div className={k.empty} data-testid="product-reviews-empty">
        <p className={k.emptyText}>{k.emptyCopy}</p>
      </div>
    );
  }

  const avg = reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length;
  return (
    <div data-testid="product-reviews-section">
      <div className="flex items-center gap-3 mb-4">
        <p className={k.title}>{k.heading(reviews.length)}</p>
        <div className="flex items-center gap-1">
          <StarDisplay value={Math.round(avg)} modern={modern} />
          <span className={k.avg}>{Number(avg ?? 0).toFixed(1)}</span>
        </div>
      </div>

      <div className="space-y-4">
        {reviews.map((r) => (
          <div key={r.id} className={k.card} data-testid={`review-${r.id}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <p className={k.author}>{r.author_display}</p>
                <StarDisplay value={r.rating} modern={modern} />
              </div>
              <p className={k.date}>{formatDateET(r.created_at)}</p>
            </div>
            <p className={`${k.body} whitespace-pre-wrap break-words`}>{r.body}</p>
            {(r.media_ids || []).length > 0 && (
              <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
                {r.media_ids.map((id) => (
                  <img
                    key={id}
                    src={`${API_URL}/api/uploads/media/${id}`}
                    alt="review"
                    className={`w-24 h-24 object-cover flex-shrink-0 ${k.img}`}
                    loading="lazy"
                  />
                ))}
              </div>
            )}
            <div className="mt-2 flex items-center justify-end">
              <FireLikeButton review={r} testId={`review-fire-${r.id}`} modern={modern} />
            </div>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes reviewFireBump {
          0%   { transform: scale(1) rotate(0deg); }
          20%  { transform: scale(1.5) rotate(-8deg); }
          45%  { transform: scale(0.9) rotate(6deg); }
          70%  { transform: scale(1.15) rotate(-3deg); }
          100% { transform: scale(1) rotate(0deg); }
        }
        .review-fire-bump {
          animation: reviewFireBump 380ms cubic-bezier(0.34, 1.56, 0.64, 1);
          transform-origin: center;
        }
      `}</style>
    </div>
  );
}
